import { supportsMealPlans } from 'common/MealPlanUtils';
import { SessionStorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { Location } from 'src/gqlReactTypings.generated.d';
import { Routes } from 'src/shared/Routes';
import { useNonstrictSessionStorage } from 'src/shared/storage/Storage';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { StringParam, useQueryParam } from 'use-query-params';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { ExploreTabNames } from './consts';

interface OneTimeOrderingToastProps {
  location: Location;
  isOnSubscriptionIntake: boolean;
}

export const ONE_TIME_ORDERING_TOAST_ID = 'ONE_TIME_ORDERING_TOAST_ID';

export const OneTimeOrderingToast: React.FC<OneTimeOrderingToastProps> = ({ location, isOnSubscriptionIntake }) => {
  const [tab] = useQueryParam(QueryParams.TAB, StringParam);
  const [, , zipCodeMetaData] = useCachedZipCode();
  const isToastOpen = toast.isActive(ONE_TIME_ORDERING_TOAST_ID);
  const regionId = get(zipCodeMetaData, 'cachedZipCodeData.zipCode.region.id', null);
  const deliveryHubId = get(zipCodeMetaData, 'cachedZipCodeData.zipCode.deliveryHubId', null);
  const exploreWeeklyShefServiceTabsEnabled = useFeatureFlag('exploreWeeklyShefServiceTabsEnabled');
  const isMealPlanRegion = supportsMealPlans({
    regionId,
    deliveryHubId,
  });
  const isOnExplorePage = locationInRoutes(location, [Routes.CONSUMER_EXPLORE]);
  const mealPlanDataLoaded = useMealPlanForUserStoreSelectors.use.mealPlanDataLoaded();
  const userHasActiveMealPlan = useMealPlanForUserStoreSelectors.use.userHasActiveMealPlan();

  const [sessionOneTimeOrderToastShown, setSessionOneTimeOrderToastShown] = useNonstrictSessionStorage<boolean>(
    SessionStorageKeys.ONE_TIME_ORDER_TOAST_SHOWN,
    undefined
  );

  /**
   * This useEffect handles displaying the toast under the following conditions:
   * 1. The user is on the explore page.
   * 2. The user is on the one-time ordering tab.
   * 3. The toast is not already showing.
   * 4. The toast has not been shown in the current session.
   * 5. The feature flag for explore weekly Shef service tabs is enabled.
   * 6. The user is in a meal plan region.
   */
  const showToastConditions =
    isOnExplorePage &&
    tab === ExploreTabNames.OneTime &&
    !isToastOpen &&
    !sessionOneTimeOrderToastShown &&
    exploreWeeklyShefServiceTabsEnabled &&
    isMealPlanRegion &&
    mealPlanDataLoaded &&
    userHasActiveMealPlan;

  useEffect(() => {
    const toastContent = (
      <div className='flex h-20 items-center justify-between gap-2 bg-warning p-2'>
        <Icon name='info' color={IconColor.WHITE} size={24} />
        <p className='text-caption-400 text-eggplant'>
          You’re now browsing dishes for one time ordering. This won’t affect your weekly service.
        </p>
        <div className='flex h-full items-start'>
          <Icon name='close' color={IconColor.WHITE} size={16} />
        </div>
      </div>
    );

    if (showToastConditions) {
      toast(toastContent, {
        autoClose: false,
        toastId: ONE_TIME_ORDERING_TOAST_ID,
        position: 'bottom-right',
        onClose: () => {
          toast.dismiss(ONE_TIME_ORDERING_TOAST_ID);
        },
      });
      setSessionOneTimeOrderToastShown(true);
    }
  }, [showToastConditions, setSessionOneTimeOrderToastShown]);

  /**
   * This useEffect handles dismissing the toast under the following conditions:
   * 1. The toast is showing.
   * 2. The user navigates to a meal plan route or the Shef service tab.
   */
  useEffect(() => {
    if ((isOnSubscriptionIntake || tab === ExploreTabNames.ShefService) && isToastOpen) {
      toast.dismiss(ONE_TIME_ORDERING_TOAST_ID);
    }
  }, [isOnSubscriptionIntake, tab, isToastOpen]);

  /**
   * This component does not render anything.
   * It handles displaying and dismissing the OneTimeOrderingToast
   */
  return null;
};
